import React from "react";
import { motion } from "framer-motion";
import { useLang } from "@/i18n/LanguageProvider";
import {
  Users2,
  UserCheck,
  Zap,
  Calendar,
  Settings2,
  ArrowUpRight,
} from "lucide-react";

const ICONS = [Users2, UserCheck, Zap, Calendar, Settings2];

export const Services = () => {
  const { t } = useLang();
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative scroll-mt-24 overflow-hidden bg-white py-24 dark:bg-[#020617] md:py-32"
    >
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#7C3AED]/30 to-transparent" />
      <div className="mx-auto max-w-7xl px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-[#7C3AED]/20 bg-[#7C3AED]/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#7C3AED]">
            {t.services.eyebrow}
          </div>
          <h2 className="mt-5 font-display text-[clamp(2rem,4.2vw,3.25rem)] font-bold leading-tight tracking-tight text-[#0F172A] dark:text-white">
            {t.services.title}
          </h2>
          <p className="mt-4 max-w-xl text-pretty text-base leading-relaxed text-slate-600 dark:text-slate-300 md:text-lg">
            {t.services.subtitle}
          </p>
        </motion.div>

        <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {t.services.items.map((s, i) => {
            const Icon = ICONS[i] || Users2;
            const isFeatured = i === 0;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.8, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                whileHover={{ y: -6, transition: { duration: 0.35, ease: [0.16, 1, 0.3, 1] } }}
                data-testid="service-card"
                className={`group relative overflow-hidden rounded-3xl border p-7 transition-colors ${
                  isFeatured
                    ? "lg:row-span-2 lg:col-span-1 border-transparent text-white shadow-elevated"
                    : "border-slate-200/80 bg-white hover:border-[#7C3AED]/40 hover:shadow-elevated dark:border-white/5 dark:bg-[#0F172A]"
                }`}
                style={
                  isFeatured
                    ? {
                        background:
                          "linear-gradient(135deg, #0F172A 0%, #1E3A8A 60%, #4C1D95 100%)",
                      }
                    : undefined
                }
              >
                {isFeatured && (
                  <div className="absolute -top-16 -right-16 h-56 w-56 rounded-full bg-[#7C3AED] opacity-30 blur-3xl" />
                )}
                <div className="relative">
                  <div
                    className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ${
                      isFeatured
                        ? "bg-white/10 text-white ring-1 ring-white/20"
                        : "bg-[#1E3A8A]/5 text-[#1E3A8A] dark:bg-white/5 dark:text-[#A5B4FC]"
                    }`}
                  >
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3
                    className={`mt-5 font-display text-xl font-bold leading-tight tracking-tight md:text-2xl ${
                      isFeatured ? "text-white" : "text-[#0F172A] dark:text-white"
                    }`}
                  >
                    {s.title}
                  </h3>
                  <p
                    className={`mt-3 text-sm leading-relaxed md:text-[15px] ${
                      isFeatured ? "text-slate-200" : "text-slate-600 dark:text-slate-300"
                    }`}
                  >
                    {s.desc}
                  </p>
                  <div
                    className={`mt-6 inline-flex items-center gap-1.5 text-sm font-semibold ${
                      isFeatured ? "text-[#C4B5FD]" : "text-[#7C3AED]"
                    }`}
                  >
                    <span>Mehr erfahren</span>
                    <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
